import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherUserCheck, FeatherUserX } from "@subframe/core";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useActivateUser, useDeactivateUser } from "@/generated/users/users";

import { accountActionErrorMessage } from "./account-action-error";
import { useInvalidateAccount } from "./use-invalidate-account";

interface ChangeAccountStatusDialogProps {
  userId: string;
  displayName: string;
  action: "activate" | "deactivate";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getCopy(action: "activate" | "deactivate", name: string) {
  if (action === "deactivate") {
    return {
      variant: "error" as const,
      icon: <FeatherUserX />,
      title: "Deactivate account?",
      body: (
        <>
          <strong>{name}</strong> will be signed out everywhere and won&apos;t
          be able to sign in until the account is reactivated. Their records are
          kept.
        </>
      ),
      confirmLabel: "Yes, deactivate",
      pendingLabel: "Deactivating...",
      successMessage: `${name}'s account has been deactivated`,
    };
  }
  return {
    variant: "success" as const,
    icon: <FeatherUserCheck />,
    title: "Reactivate account?",
    body: (
      <>
        <strong>{name}</strong> will be able to sign in again with their
        existing password or PIN.
      </>
    ),
    confirmLabel: "Yes, reactivate",
    pendingLabel: "Reactivating...",
    successMessage: `${name}'s account has been reactivated`,
  };
}

/** Confirm before deactivating or reactivating someone's account. */
export function ChangeAccountStatusDialog({
  userId,
  displayName,
  action,
  open,
  onOpenChange,
}: ChangeAccountStatusDialogProps) {
  const activate = useActivateUser();
  const deactivate = useDeactivateUser();
  const invalidateAccount = useInvalidateAccount();
  const mutation = action === "activate" ? activate : deactivate;
  const copy = getCopy(action, displayName);

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync({ id: userId });
      toast.success(copy.successMessage);
      // Close before the refetch flips `action` under the open dialog.
      onOpenChange(false);
      await invalidateAccount(userId);
    } catch (error) {
      toast.error(
        accountActionErrorMessage(
          error,
          `Could not ${action} ${displayName}'s account`,
        ),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">{copy.title}</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant={copy.variant}
                size="x-large"
                icon={copy.icon}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  {copy.title}
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  {copy.body}
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant={
                action === "deactivate"
                  ? "destructive-primary"
                  : "neutral-primary"
              }
              size="extra-large"
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? copy.pendingLabel : copy.confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
